import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { pagesForSources, employeeVisiblePages } from "@prism/shared/modules";
import { isAdmin } from "@prism/shared/constants";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary.jsx";
import { ProtectedRoute } from "./components/ProtectedRoute.jsx";
import { ViewAsBanner, ViewAsError } from "./components/ViewAsBanner.jsx";
import { DASHBOARD_PAGE_KEYS, pageKeyFromPath } from "./lib/pages.js";
import { rangeDays, rangeLabel } from "./lib/time.js";
import { useApi } from "./hooks/useApi.js";
import { AdminRoute } from "./components/AdminRoute.jsx";
import { BillingRoute } from "./components/BillingRoute.jsx";
import { StaffRoute } from "./components/StaffRoute.jsx";
import { PageLoading } from "./components/index.js";
import { DashboardLayout } from "./layouts/DashboardLayout.jsx";
import { useSyncStatus } from "./hooks/useSyncStatus.js";
import { useActiveClient } from "./hooks/useActiveClient.js";
import { SyncStatusContext } from "./context/SyncStatusContext.js";
import { Login } from "./pages/Login.jsx";
import { ForgotPassword } from "./pages/ForgotPassword.jsx";
import { SetPassword, ResetPassword } from "./pages/SetPassword.jsx";
import { AcceptProposal } from "./pages/AcceptProposal.jsx";
import { SignContract } from "./pages/SignContract.jsx";
import { Overview } from "./pages/Overview.jsx";
import { Traffic } from "./pages/Traffic.jsx";
import { Ads } from "./pages/Ads.jsx";
import { Conversions } from "./pages/Conversions.jsx";
import { Sales } from "./pages/Sales.jsx";
import { Account } from "./pages/Account.jsx";
import { Profile } from "./pages/Profile.jsx";
import { AdminClients } from "./pages/AdminClients.jsx";
import { AdminOrganization } from "./pages/AdminOrganization.jsx";
import { NewClient } from "./pages/NewClient.jsx";
import { ClientDetail } from "./pages/ClientDetail.jsx";
import { Seo } from "./pages/Seo.jsx";
import { AiVisibility } from "./pages/AiVisibility.jsx";
import { OurServices } from "./pages/OurServices.jsx";
import { ClientHome } from "./pages/ClientHome.jsx";
import { MyClients } from "./pages/MyClients.jsx";
import { Social } from "./pages/Social.jsx";
import { AdminTeam } from "./pages/AdminTeam.jsx";
import { AgencyHome } from "./pages/AgencyHome.jsx";
import { ProspectPipeline } from "./pages/ProspectPipeline.jsx";
import { AgencyContracts } from "./pages/AgencyContracts.jsx";
import { AgencyProposals } from "./pages/AgencyProposals.jsx";
import { ProposalBuilder } from "./pages/ProposalBuilder.jsx";
import { ClientCockpit } from "./pages/ClientCockpit.jsx";
import { AgencyBilling } from "./pages/AgencyBilling.jsx";
import { AgencyOrders } from "./pages/AgencyOrders.jsx";
import { AdminCatalog } from "./pages/AdminCatalog.jsx";
import { AgencyWork } from "./pages/AgencyWork.jsx";
import { PortalWork } from "./pages/PortalWork.jsx";

/**
 * Per-page chrome metadata. The key matches the page key from the shared
 * module registry. Labels come from there; subtitles are frontend copy.
 * A subtitle may be a function of the active date range ({ days, label,
 * explicit }) so header copy tracks the DateRangePicker instead of
 * hardcoding "28 days". `explicit` is false when the URL carries no
 * ?from=/?to= (the backend default window applies).
 */
const PAGE_META = {
  home:            { subtitle: "Your account at a glance" },
  agency:            { subtitle: "Your agency at a glance" },
  "agency-pipeline": { subtitle: "Prospect pipeline · new business" },
  "agency-proposals": { subtitle: "Proposals · scope & pricing" },
  "agency-contracts": { subtitle: "Client contracts · renewals & scope" },
  "agency-work":     { subtitle: "Work orders · tasks · deliverables" },
  "agency-orders":   { subtitle: "Print & promotional orders · quote, approval, billing handoff" },
  overview:        { subtitle: ({ label }) => label },
  traffic:         { subtitle: "Sources · geography · landing pages" },
  ads:             { subtitle: "Search + Performance Max campaigns" },
  conversions:     { subtitle: "Funnel · forms · revenue attribution" },
  sales:           { subtitle: ({ days }) => `All channels · ${days} days` },
  seo:             { subtitle: "Search Console · queries · rankings" },
  "ai-visibility": { subtitle: "How AI engines recommend your business" },
  profile:         { subtitle: "Your name, role & password" },
  account:         { subtitle: "Business · users · billing" },
  "our-services":  { subtitle: "What we're delivering for you" },
  work:            { subtitle: "What we're working on and what's ready" },
  social:          { subtitle: ({ days }) => `Social performance · last ${days} days` },
  "my-clients":    { subtitle: "Your assigned clients" },
  clients:         { subtitle: "Staff cockpit · health, contract & goals" },
  admin:           { subtitle: "Manage all clients · admin only" },
  "admin-organization": { subtitle: "Agency identity, brands & capabilities · super admin only" },
  "admin-team":    { subtitle: "BMG team & access · super admin only" },
  "agency-billing": { subtitle: "Billing desk · what to invoice, what's unpaid" },
  "admin-catalog": { subtitle: "Services & prices · what quick-add offers" },
};

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/login"            element={<Login />} />
        <Route path="/forgot-password"  element={<ForgotPassword />} />
        <Route path="/set-password"     element={<SetPassword />} />
        <Route path="/reset-password"   element={<ResetPassword />} />
        <Route path="/accept-proposal"  element={<AcceptProposal />} />
        <Route path="/sign-contract"    element={<SignContract />} />

        {/* Protected — nested under ProtectedRoute + Shell */}
        <Route
          element={
            <ProtectedRoute>
              <Shell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/home"        element={<ClientHomeRoute />} />
          <Route path="/my-clients"  element={<MyClients />} />
          {/* Per-client staff cockpit — NOT under /admin: EMPLOYEEs reach it
              for their scoped clients (backend enforces the scope). */}
          <Route path="/clients/:id" element={<StaffRoute><ClientCockpit /></StaffRoute>} />
          <Route path="/agency"          element={<AdminRoute><AgencyHome /></AdminRoute>} />
          <Route path="/agency/pipeline" element={<AdminRoute><ProspectPipeline /></AdminRoute>} />
          <Route path="/agency/contracts" element={<AdminRoute allow={["ACCOUNTANT"]}><AgencyContracts /></AdminRoute>} />
          <Route path="/agency/proposals"     element={<AdminRoute><AgencyProposals /></AdminRoute>} />
          <Route path="/agency/proposals/:id" element={<AdminRoute><ProposalBuilder /></AdminRoute>} />
          <Route path="/agency/billing" element={<BillingRoute><AgencyBilling /></BillingRoute>} />
          <Route path="/agency/orders" element={<AdminRoute><AgencyOrders /></AdminRoute>} />
          <Route path="/agency/work" element={<StaffRoute><AgencyWork /></StaffRoute>} />
          <Route path="/overview"    element={<Overview />} />
          <Route path="/traffic"     element={<Traffic />} />
          <Route path="/ads"         element={<Ads />} />
          <Route path="/conversions" element={<Conversions />} />
          <Route path="/sales"         element={<Sales />} />
          <Route path="/seo"           element={<Seo />} />
          <Route path="/social"        element={<Social />} />
          <Route path="/ai-visibility" element={<AiVisibility />} />
          <Route path="/our-services"  element={<OurServices />} />
          <Route path="/work"          element={<PortalWork />} />
          <Route path="/profile"       element={<Profile />} />
          <Route path="/account"       element={<Account />} />
          <Route path="/admin/team" element={
            <AdminRoute requireSuper><AdminTeam /></AdminRoute>
          } />
          <Route path="/admin/clients"     element={
            <AdminRoute><AdminClients /></AdminRoute>
          } />
          <Route path="/admin/clients/new" element={
            <AdminRoute><NewClient /></AdminRoute>
          } />
          <Route path="/admin/clients/:id" element={
            <AdminRoute><ClientDetail /></AdminRoute>
          } />
          <Route path="/admin/organization" element={
            <AdminRoute requireSuper><AdminOrganization /></AdminRoute>
          } />
          {/* Change #1: the Divisions page folded into Organization. Keep the old
              path working so existing bookmarks and links don't 404. */}
          <Route path="/admin/divisions" element={<Navigate to="/admin/organization" replace />} />
          <Route path="/admin/catalog" element={
            <BillingRoute><AdminCatalog /></BillingRoute>
          } />
        </Route>

        {/* Unknown paths — route through the role-aware home redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

/**
 * Role-aware landing. EMPLOYEEs go to their assigned-clients list; everyone
 * else (admins, client users) goes to the Overview dashboard.
 */
function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "ACCOUNTANT") return <Navigate to="/agency/billing" replace />;
  if (user?.role === "EMPLOYEE") return <EmployeeLanding />;
  if (user?.role === "CLIENT" || user?.role === "OWNER") return <Navigate to="/home" replace />;
  // SUPER_ADMIN, ADMIN
  return <Navigate to="/agency" replace />;
}

/** EMPLOYEEs with open tasks land on My Tasks; otherwise My Clients (spec §5). */
function EmployeeLanding() {
  const { data, loading } = useApi("/api/work/my-tasks");
  if (loading && !data) return <PageLoading />;
  return <Navigate to={data?.tasks?.length ? "/agency/work?view=mine" : "/my-clients"} replace />;
}

/**
 * /home is the client account page. Admins have no Home tab and shouldn't land
 * on a client relationship view — a direct visit bounces them to the dashboard.
 */
function ClientHomeRoute() {
  const { user } = useAuth();
  if (isAdmin(user?.role)) return <Navigate to="/overview" replace />;
  return <ClientHome />;
}

/**
 * Authenticated dashboard shell. Pulls client from auth context, derives
 * which pages to show from the client's connected data sources via the
 * shared module registry, and renders the matched child route via <Outlet>.
 */
function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // home client OR view-as target; error = bad/stale ?clientId (client removed
  // or out of scope) — surfaced below instead of silently collapsing the UI.
  const { client, isViewingAs, error: activeClientError } = useActiveClient();
  const syncStatus = useSyncStatus();

  const role = user?.role;
  const connectedSources = (client?.dataSources || []).map((ds) => ds.type);

  let pages;
  if (role === "EMPLOYEE") {
    // Employees navigate via /my-clients; dashboard tabs appear only while
    // viewing a scoped client, filtered by their dataScopes. Account always shows.
    const dash = isViewingAs && client
      ? pagesForSources(connectedSources).filter(
          (p) => p.group === "account" || employeeVisiblePages(user?.dataScopes).has(p.key)
        )
      : [];
    pages = [{ key: "my-clients", label: "My Clients", group: "main" }, { key: "agency-work", label: "Work", group: "main" }, ...dash];
    // Settings group: My Profile is always available (it's about the logged-in
    // user, not a client); Client Settings shows whichever client is in view.
    // When viewing a scoped client, `dash` already carries both from the
    // registry — these guards just cover the no-client-in-view case.
    if (!pages.some((p) => p.key === "profile")) {
      pages.push({ key: "profile", label: "My Profile", group: "account" });
    }
    if (!pages.some((p) => p.key === "account")) {
      pages.push({ key: "account", label: "Client Settings", group: "account" });
    }
  } else if (role === "ACCOUNTANT") {
    pages = [
      { key: "agency-billing", label: "Billing", group: "main" },
      { key: "agency-contracts", label: "Contracts", group: "main" },
      { key: "admin-catalog", label: "Catalog", group: "main" },
      { key: "profile", label: "My Profile", group: "account" },
    ];
  } else if (isAdmin(role)) {
    pages = [
      { key: "agency", label: "Agency Home", group: "main" },
      { key: "agency-work", label: "Work", group: "main" },
      ...pagesForSources(connectedSources),
      { key: "admin", label: "Clients", group: "admin" },
      { key: "agency-proposals", label: "Proposals", group: "admin" },
      { key: "agency-contracts", label: "Contracts", group: "admin" },
      { key: "agency-orders", label: "Orders", group: "admin" },
      { key: "admin-catalog", label: "Catalog", group: "admin" },
      { key: "agency-billing", label: "Billing", group: "admin" },
      ...(role === "SUPER_ADMIN" ? [{ key: "admin-organization", label: "Organization", group: "admin" }] : []),
      ...(role === "SUPER_ADMIN" ? [{ key: "admin-team", label: "Team", group: "admin" }] : []),
    ];
  } else {
    // Client users: account home + dashboard + read-only "Our Services".
    pages = [
      { key: "home", label: "Home", group: "main" },
      ...pagesForSources(connectedSources),
      { key: "our-services", label: "Our Services", group: "main" },
      { key: "work", label: "Work", group: "main" },
    ];
  }

  // Hide the bottom client-identity card for employees who aren't viewing a
  // specific client (their anchor client isn't theirs to see).
  const layoutClient = role === "EMPLOYEE" && !isViewingAs ? null : client;

  // Derive activePage key from the URL ("/traffic" → "traffic"; admin/agency
  // sub-pages disambiguate on the second segment) — shared with the
  // ClientSwitcher via lib/pages.js. Dashboard pages are the ones the shared
  // module registry drives; everything else is route-only chrome.
  const activePage = pageKeyFromPath(location.pathname);
  const activePageDef = pages.find((p) => p.key === activePage);
  const meta = PAGE_META[activePage];
  // Resolve range-aware subtitles against the actual ?from=/?to= in the URL
  // so the header stays honest when the DateRangePicker changes the window.
  const rangeParams = new URLSearchParams(location.search);
  const range = {
    days: rangeDays(rangeParams.get("from"), rangeParams.get("to")),
    label: rangeLabel(rangeParams.get("from"), rangeParams.get("to")),
    explicit: rangeParams.has("from"),
  };
  const subtitle = typeof meta?.subtitle === "function" ? meta.subtitle(range) : meta?.subtitle;
  const isNonDashboardPage = !DASHBOARD_PAGE_KEYS.has(activePage);
  const headerTitle =
    activePageDef?.label ||
    (activePage === "admin" ? "Clients" : activePage === "clients" ? "Client Cockpit" : "Prism");

  // Staff-only "Viewing as" chrome. role home = where Exit (and a failed
  // client fetch's Back action) should land.
  const roleHome = role === "EMPLOYEE" ? "/my-clients" : role === "ACCOUNTANT" ? "/agency/billing" : "/agency";
  const roleHomeLabel = role === "EMPLOYEE" ? "Back to My Clients" : role === "ACCOUNTANT" ? "Back to Billing" : "Back to Agency";

  return (
    <SyncStatusContext.Provider value={syncStatus}>
      <DashboardLayout
        pages={pages}
        activePage={activePage}
        onNavigate={(key) => {
          // "My Clients" returns to the list — drop the view-as client param.
          if (key === "my-clients") return navigate("/my-clients");
          const path =
            key === "admin" ? "/admin/clients"
            : key === "admin-organization" ? "/admin/organization"
            : key === "admin-team" ? "/admin/team"
            : key === "agency" ? "/agency"
            : key === "agency-proposals" ? "/agency/proposals"
            : key === "agency-contracts" ? "/agency/contracts"
            : key === "agency-billing" ? "/agency/billing"
            : key === "agency-work" ? "/agency/work"
            : key === "agency-orders" ? "/agency/orders"
            : key === "admin-catalog" ? "/admin/catalog"
            : `/${key}`;
          // Preserve view-as (?clientId) + date range (?from/?to) across tab
          // navigation, so switching tabs keeps the selected client instead of
          // snapping back to the home client.
          navigate(`${path}${location.search}`);
        }}
        client={layoutClient}
        syncStatus={syncStatus}
        pageTitle={headerTitle}
        pageSubtitle={
          subtitle
            // My Profile is about the logged-in user, not the viewed client —
            // don't append the client name there.
            ? `${subtitle}${activePage !== "profile" && layoutClient?.name ? ` · ${layoutClient.name}` : ""}`
            : null
        }
        showDatePicker={!isNonDashboardPage}
      >
        {isViewingAs && activeClientError ? (
          // Bad/stale ?clientId — show a clear way out instead of rendering
          // pages against a client that doesn't resolve.
          <ViewAsError
            error={activeClientError}
            backLabel={roleHomeLabel}
            onBack={() => navigate(roleHome)}
          />
        ) : (
          <>
            {isViewingAs && client && (
              <ViewAsBanner
                clientName={client.name}
                onExit={() => navigate(roleHome)}
              />
            )}
            {/* Keyed on pathname: a crash on one page doesn't poison the
                boundary for the rest of the session — navigating away remounts
                it, so the sidebar stays usable and the next page renders
                normally. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </>
        )}
      </DashboardLayout>
    </SyncStatusContext.Provider>
  );
}
